import { Truck, MapPin, Plane, PackageSearch, Users, Split } from "lucide-react";

export function Transport({ lr, pickups, deliveries, carrier, hasCarrier, hirer, hasHirer }) {
  return (
    <>
      <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-transport">
        <div className="section-title flex items-center gap-2"><Truck className="w-4 h-4" /> Transport &amp; Carrier</div>
        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <div><span className="text-[#68665E]">Mode:</span> <span className="font-medium">{lr.transport_mode === "Air" ? "By Air (AWB)" : `By Road${lr.road_load_type ? ` (${lr.road_load_type})` : ""}`}</span></div>
          {carrier.company && <div><span className="text-[#68665E]">Sub-Carrier:</span> <span className="font-medium">{carrier.company}</span></div>}
        </div>
        {(() => {
          const origin = (pickups[0]?.address || pickups[0]?.company || lr.from_location || "").trim();
          const dest = (deliveries[deliveries.length - 1]?.address || deliveries[deliveries.length - 1]?.company || lr.to_location || "").trim();
          if (!origin && !dest) return null;
          const url = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}`;
          return (
            <a className="btn-secondary text-xs mt-3 inline-flex" href={url} target="_blank" rel="noreferrer" data-testid="lr-route-maps-link">
              <MapPin className="w-3 h-3" /> View route on Google Maps
            </a>
          );
        })()}
        {hasCarrier && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm mt-2">
            {(carrier.pickup_poc_name || carrier.pickup_poc_phone) && <div><span className="text-[#68665E]">Pickup POC:</span> {carrier.pickup_poc_name} {carrier.pickup_poc_phone ? `· ${carrier.pickup_poc_phone}` : ""}</div>}
            {(carrier.delivery_poc_name || carrier.delivery_poc_phone) && <div><span className="text-[#68665E]">Delivery POC:</span> {carrier.delivery_poc_name} {carrier.delivery_poc_phone ? `· ${carrier.delivery_poc_phone}` : ""}</div>}
            {carrier.notes && <div className="md:col-span-2"><span className="text-[#68665E]">Notes:</span> {carrier.notes}</div>}
          </div>
        )}
        {lr.transport_mode === "Air" && (
          <div className="mt-3 border-t border-[#F0EDE5] pt-3" data-testid="lr-detail-air-tracking">
            <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm mb-2">
              {lr.flight_number && <div><span className="text-[#68665E]">Flight:</span> <span className="font-mono font-medium">{lr.flight_number}</span></div>}
              {lr.awb_number && <div><span className="text-[#68665E]">AWB:</span> <span className="font-mono font-medium">{lr.awb_number}</span></div>}
            </div>
            <div className="flex flex-wrap gap-2">
              {lr.flight_number && (
                <a className="btn-secondary text-xs" href={`https://www.flightaware.com/live/flight/${encodeURIComponent(lr.flight_number.replace(/\s/g, ""))}`} target="_blank" rel="noreferrer" data-testid="lr-track-flight"><Plane className="w-3 h-3" /> Live Flight Tracking</a>
              )}
              {lr.awb_number && (
                <a className="btn-secondary text-xs" href={`https://www.google.com/search?q=${encodeURIComponent(lr.awb_number + " air waybill AWB tracking")}`} target="_blank" rel="noreferrer" data-testid="lr-track-awb"><PackageSearch className="w-3 h-3" /> Live AWB Tracking</a>
              )}
              {!lr.flight_number && !lr.awb_number && <span className="text-[11px] text-[#A09E96]">Add a Flight Number / AWB Number to enable live tracking.</span>}
            </div>
          </div>
        )}
      </div>

      {hasHirer && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-hirer">
          <div className="section-title flex items-center gap-2"><Users className="w-4 h-4" /> Hirer / Client <span className="text-[11px] font-normal text-[#68665E]">(records only — not printed on LR)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
            <div><span className="text-[#68665E]">Company:</span> <span className="font-medium">{hirer.name || "—"}</span></div>
            {hirer.gst && <div><span className="text-[#68665E]">GST:</span> <span className="font-mono">{hirer.gst}</span></div>}
            {hirer.address && <div className="md:col-span-2"><span className="text-[#68665E]">Address:</span> {hirer.address}</div>}
            {hirer.division && <div><span className="text-[#68665E]">Department:</span> {hirer.division}</div>}
            {hirer.division_address && <div><span className="text-[#68665E]">Department Address:</span> {hirer.division_address}</div>}
            {(hirer.contact_name || hirer.contact_phone || hirer.contact_email) && <div className="md:col-span-2"><span className="text-[#68665E]">Contact:</span> {hirer.contact_name} {hirer.contact_phone ? `· ${hirer.contact_phone}` : ""} {hirer.contact_email ? `· ${hirer.contact_email}` : ""}</div>}
          </div>
        </div>
      )}

      {lr.road_load_type === "PTL" && (lr.ptl_legs || []).length > 0 && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-ptl-legs">
          <div className="section-title flex items-center gap-2"><Split className="w-4 h-4" /> PTL Transfer Legs</div>
          <ol className="space-y-2">
            {lr.ptl_legs.map((leg, i) => (
              <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-testid={`lr-detail-ptl-leg-${i}`}>
                <span className="w-6 h-6 rounded-full bg-[#F5F3EC] text-[#68665E] text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                <span className="font-medium text-[#1A1A18]">{leg.mode || "—"}</span>
                {leg.carrier && <span className="text-[#68665E]">· {leg.carrier}</span>}
                {leg.vehicle_number && <span className="font-mono text-xs text-[#68665E]">· {leg.vehicle_number}</span>}
                <span className="text-[#1A1A18]">· {leg.from_location || "?"} → {leg.to_location || "?"}</span>
                {leg.notes && <span className="text-[#A09E96] text-xs">· {leg.notes}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </>
  );
}
